import { useState } from "react";
import { Check, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, Panel } from "@/components/app/kit";
import { ModuleSelector } from "@/components/platform/ModuleSelector";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d) => new Date(Date.now() + d * 864e5).toISOString().slice(0, 10);
const SLUG = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;
const HOST = /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const slugify = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export const emptyTenant = (ref) => {
  const c = ref.countries.find((x) => x.code === "FR") || ref.countries[0] || {};
  const p = ref.plans.find((x) => x.active) || ref.plans[0] || { limits: {}, modules: [] };
  return {
    name: "", commercial_name: "", logo_data: null, establishment_type: "cabinet_individuel", specialties: [],
    country_code: c.code || "FR", city: "", address: "", postal_code: "", phone: "", email: "", website: "",
    legal_representative: { full_name: "", email: "", phone: "" }, medical_director: { full_name: "", email: "", phone: "" },
    legal_form: "", registration_number: "", tax_id: "", professional_id: "",
    admin_name: "", admin_email: "", admin_phone: "", slug: "", subdomain: "", custom_domain: "",
    currency: c.currency || "EUR", language: c.language || "fr", timezone: c.timezone || "Europe/Paris", locale: c.locale || "fr-FR",
    plan_code: p.code, status: "trial", start_date: today(), end_date: addDays(p.trial_days || 30),
    max_users: p.limits.max_users || 5, max_patients: p.limits.max_patients ?? "", storage_mb: p.limits.storage_mb || 1024,
    modules: p.modules || [],
  };
};

const STEPS = [
  { key: "identity", label: "Identité" },
  { key: "contact", label: "Coordonnées" },
  { key: "legal", label: "Responsables et légal" },
  { key: "access", label: "Administrateur et accès", createOnly: true },
  { key: "subscription", label: "Régional et abonnement" },
  { key: "modules", label: "Modules autorisés", createOnly: true },
];

const validators = {
  identity: (f) => ({ name: f.name.trim().length < 2 && "Nom requis (2 caractères minimum)", establishment_type: !f.establishment_type && "Type requis" }),
  contact: (f) => ({
    city: !f.city.trim() && "Ville requise", address: !f.address.trim() && "Adresse requise",
    phone: f.phone.trim().length < 4 && "Téléphone requis", email: !EMAIL.test(f.email) && "E-mail invalide",
  }),
  legal: (f) => ({
    "legal_representative.email": f.legal_representative.email && !EMAIL.test(f.legal_representative.email) && "E-mail invalide",
    "medical_director.email": f.medical_director.email && !EMAIL.test(f.medical_director.email) && "E-mail invalide",
  }),
  access: (f) => ({
    admin_name: f.admin_name.trim().length < 2 && "Nom requis", admin_email: !EMAIL.test(f.admin_email) && "E-mail invalide",
    slug: !SLUG.test(f.slug) && "3 à 40 caractères : minuscules, chiffres, tirets",
    subdomain: !SLUG.test(f.subdomain) && "3 à 40 caractères : minuscules, chiffres, tirets",
    custom_domain: f.custom_domain && !HOST.test(f.custom_domain) && "Nom de domaine invalide",
  }),
  subscription: (f) => ({
    plan_code: !f.plan_code && "Formule requise", start_date: !f.start_date && "Date requise",
    end_date: f.end_date && f.end_date < f.start_date && "Doit être postérieure à la date de début",
    max_users: !(Number(f.max_users) >= 1) && "Minimum 1", timezone: !f.timezone && "Fuseau requis",
  }),
  modules: () => ({}),
};

const clean = (errs) => Object.fromEntries(Object.entries(errs).filter(([, v]) => v));

const Sel = ({ value, onChange, options, testid, placeholder }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger className="bg-white" data-testid={testid}><SelectValue placeholder={placeholder || "Sélectionner"} /></SelectTrigger>
    <SelectContent className="max-h-72">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const TenantForm = ({ reference, initial, mode = "create", onSubmit }) => {
  const [f, setF] = useState(initial);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState({});
  const steps = STEPS.filter((s) => mode === "create" || !s.createOnly);
  const country = reference.countries.find((c) => c.code === f.country_code) || {};
  const plan = reference.plans.find((p) => p.code === f.plan_code) || { modules: [] };

  const set = (path, value) => setF((prev) => {
    const next = { ...prev };
    const [a, b] = path.split(".");
    if (b) next[a] = { ...prev[a], [b]: value };
    else next[a] = value;
    return next;
  });
  const err = (k) => errors[k] || (avail[k] === false ? "Déjà utilisé" : null);
  const inp = (k, props = {}) => (
    <Input value={k.includes(".") ? f[k.split(".")[0]][k.split(".")[1]] || "" : f[k] ?? ""} onChange={(e) => set(k, e.target.value)}
      className={cn("bg-white", err(k) && "border-red-400")} data-testid={`tenant-${k.replace(".", "-")}-input`} {...props} />
  );

  const onCountry = (code) => {
    const c = reference.countries.find((x) => x.code === code) || {};
    setF((p) => ({ ...p, country_code: code, currency: c.currency || p.currency, timezone: c.timezone || p.timezone, locale: c.locale || p.locale, language: c.language || p.language }));
  };
  const onPlan = (code) => {
    const p = reference.plans.find((x) => x.code === code);
    setF((prev) => ({ ...prev, plan_code: code, max_users: p.limits.max_users, max_patients: p.limits.max_patients ?? "", storage_mb: p.limits.storage_mb,
      ...(mode === "create" ? { modules: p.modules } : {}) }));
  };
  const onLogo = (file) => {
    if (!file) return;
    if (file.size > 280000) return toast.error("Logo trop volumineux (280 Ko maximum)");
    const r = new FileReader();
    r.onload = () => set("logo_data", r.result);
    r.readAsDataURL(file);
  };
  const checkAvail = async (key, value) => {
    if (!value) return;
    try {
      const { data } = await api.get("/platform/check-availability", { params: { [key]: value } });
      setAvail((a) => ({ ...a, [key]: data[key] }));
    } catch (e) { /* availability is re-checked server-side on submit */ }
  };

  const validate = (i) => {
    const e = clean(validators[steps[i].key](f));
    setErrors(e);
    const taken = steps[i].key === "access" && ["slug", "subdomain", "custom_domain", "admin_email"].some((k) => avail[k] === false);
    return !Object.keys(e).length && !taken;
  };
  const next = () => validate(step) && setStep((s) => Math.min(s + 1, steps.length - 1));

  const submit = async () => {
    for (let i = 0; i < steps.length; i += 1) {
      if (!validate(i)) return setStep(i);
    }
    setBusy(true);
    try {
      const payload = { ...f, max_users: Number(f.max_users), storage_mb: Number(f.storage_mb), max_patients: f.max_patients === "" ? null : Number(f.max_patients),
        end_date: f.end_date || null, custom_domain: f.custom_domain || null };
      ["legal_representative", "medical_director"].forEach((k) => { if (!payload[k].email) payload[k] = { ...payload[k], email: null }; });
      if (mode !== "create") ["admin_name", "admin_email", "admin_phone", "slug", "subdomain", "custom_domain", "modules", "status"].forEach((k) => delete payload[k]);
      await onSubmit(payload);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const k = steps[step].key;
  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6">
      <ol className="flex lg:flex-col gap-2 overflow-x-auto pb-2 lg:pb-0" data-testid="tenant-form-steps">
        {steps.map((s, i) => (
          <li key={s.key}>
            <button type="button" onClick={() => (i < step || validate(step)) && setStep(i)} data-testid={`tenant-step-${s.key}`}
              className={cn("flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-sm text-left whitespace-nowrap transition-colors",
                i === step ? "bg-white border border-sky-200 shadow-sm text-slate-900 font-semibold" : "text-slate-500 hover:bg-white/60")}>
              <span className={cn("h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                i < step ? "bg-emerald-500 text-white" : i === step ? "bg-sky-600 text-white" : "bg-slate-200 text-slate-600")}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              {s.label}
            </button>
          </li>
        ))}
      </ol>
      <Panel title={steps[step].label}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" key={k}>
          {k === "identity" && (
            <>
              <Field label="Nom du cabinet" required error={err("name")}>{inp("name")}</Field>
              <Field label="Nom commercial">{inp("commercial_name")}</Field>
              <Field label="Type d'établissement" required error={err("establishment_type")}>
                <Sel value={f.establishment_type} onChange={(v) => set("establishment_type", v)} options={reference.establishment_types.map((e) => [e.code, e.name])} testid="tenant-establishment-select" />
              </Field>
              <Field label="Logo" hint="PNG, JPG ou SVG — 280 Ko maximum">
                <label className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 cursor-pointer hover:border-sky-400">
                  {f.logo_data ? <img src={f.logo_data} alt="" className="h-9 w-9 rounded object-cover" /> : <ImagePlus className="h-5 w-5 text-slate-400" />}
                  <span className="text-sm text-slate-500">{f.logo_data ? "Remplacer le logo" : "Choisir un fichier"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onLogo(e.target.files?.[0])} data-testid="tenant-logo-input" />
                </label>
              </Field>
              <Field label="Spécialités" className="md:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {reference.specialties.map((s) => {
                    const on = f.specialties.includes(s);
                    return (
                      <button type="button" key={s} onClick={() => set("specialties", on ? f.specialties.filter((x) => x !== s) : [...f.specialties, s])}
                        className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", on ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-sky-300")}
                        data-testid={`tenant-specialty-${slugify(s)}`}>{s}</button>
                    );
                  })}
                </div>
              </Field>
            </>
          )}
          {k === "contact" && (
            <>
              <Field label="Pays" required>
                <Sel value={f.country_code} onChange={onCountry} options={reference.countries.map((c) => [c.code, c.name])} testid="tenant-country-select" />
              </Field>
              <Field label="Ville" required error={err("city")}>{inp("city")}</Field>
              <Field label="Adresse" required error={err("address")} className="md:col-span-2">{inp("address")}</Field>
              <Field label="Code postal">{inp("postal_code")}</Field>
              <Field label="Téléphone" required error={err("phone")} hint={country.phone_prefix && `Indicatif ${country.phone_prefix}`}>{inp("phone")}</Field>
              <Field label="E-mail du cabinet" required error={err("email")}>{inp("email", { type: "email" })}</Field>
              <Field label="Site web">{inp("website")}</Field>
            </>
          )}
          {k === "legal" && (
            <>
              <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Responsable légal</p>
              <Field label="Nom complet">{inp("legal_representative.full_name")}</Field>
              <Field label="E-mail" error={err("legal_representative.email")}>{inp("legal_representative.email")}</Field>
              <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500 pt-2">Responsable médical</p>
              <Field label="Nom complet">{inp("medical_director.full_name")}</Field>
              <Field label="E-mail" error={err("medical_director.email")}>{inp("medical_director.email")}</Field>
              <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500 pt-2">Informations légales ({country.name})</p>
              <Field label="Forme juridique">{inp("legal_form")}</Field>
              <Field label={country.registration_label || "N° d'immatriculation"}>{inp("registration_number")}</Field>
              <Field label={country.tax_label || "Identifiant fiscal"}>{inp("tax_id")}</Field>
              <Field label={country.professional_label || "N° d'ordre professionnel"}>{inp("professional_id")}</Field>
            </>
          )}
          {k === "access" && (
            <>
              <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Premier administrateur du cabinet</p>
              <Field label="Nom complet" required error={err("admin_name")}>{inp("admin_name")}</Field>
              <Field label="E-mail" required error={errors.admin_email || (avail.admin_email === false ? "Adresse réservée à un administrateur plateforme" : null)}>
                {inp("admin_email", { type: "email", onBlur: () => checkAvail("admin_email", f.admin_email) })}
              </Field>
              <Field label="Téléphone">{inp("admin_phone")}</Field>
              <div />
              <p className="md:col-span-2 text-xs font-semibold uppercase tracking-wider text-slate-500 pt-2">Accès technique</p>
              <Field label="Identifiant technique unique" required error={err("slug")} hint="Immuable après création">
                <div className="flex gap-2">
                  {inp("slug", { onBlur: () => checkAvail("slug", f.slug), className: cn("bg-white font-mono", err("slug") && "border-red-400") })}
                  <Button type="button" variant="outline" onClick={() => { const s = slugify(f.name); setF((p) => ({ ...p, slug: s, subdomain: p.subdomain || s })); checkAvail("slug", s); }} data-testid="tenant-slug-generate">Générer</Button>
                </div>
              </Field>
              <Field label="Sous-domaine souhaité" required error={err("subdomain")} hint={f.subdomain && `${f.subdomain}.${reference.base_domain}`}>
                {inp("subdomain", { onBlur: () => checkAvail("subdomain", f.subdomain), className: cn("bg-white font-mono", err("subdomain") && "border-red-400") })}
              </Field>
              <Field label="Domaine personnalisé (facultatif)" error={err("custom_domain")} hint="Vérification DNS requise après création" className="md:col-span-2">
                {inp("custom_domain", { placeholder: "www.mon-cabinet.fr", onBlur: () => checkAvail("custom_domain", f.custom_domain), className: "bg-white font-mono" })}
              </Field>
            </>
          )}
          {k === "subscription" && (
            <>
              <Field label="Devise" required><Sel value={f.currency} onChange={(v) => set("currency", v)} options={reference.currencies.map((c) => [c, c])} testid="tenant-currency-select" /></Field>
              <Field label="Langue" required><Sel value={f.language} onChange={(v) => set("language", v)} options={reference.languages.filter((l) => l.available).map((l) => [l.code, l.name])} testid="tenant-language-select" /></Field>
              <Field label="Fuseau horaire" required error={err("timezone")}><Sel value={f.timezone} onChange={(v) => set("timezone", v)} options={reference.timezones.map((z) => [z, z])} testid="tenant-timezone-select" /></Field>
              <Field label="Paramètres régionaux (locale)" required>{inp("locale")}</Field>
              <Field label="Formule d'abonnement" required error={err("plan_code")}><Sel value={f.plan_code} onChange={onPlan} options={reference.plans.map((p) => [p.code, p.name])} testid="tenant-plan-select" /></Field>
              {mode === "create" && (
                <Field label="Statut initial" required>
                  <Sel value={f.status} onChange={(v) => set("status", v)} options={[["trial", "Essai"], ["active", "Actif"], ["suspended", "Suspendu"]]} testid="tenant-status-select" />
                </Field>
              )}
              <Field label="Date de début" required error={err("start_date")}>{inp("start_date", { type: "date" })}</Field>
              <Field label={f.status === "trial" ? "Fin de l'essai" : "Date de fin"} error={err("end_date")} hint="Laisser vide pour une durée indéterminée">{inp("end_date", { type: "date" })}</Field>
              <Field label="Utilisateurs maximum" required error={err("max_users")}>{inp("max_users", { type: "number", min: 1 })}</Field>
              <Field label="Patients maximum" hint="Vide = illimité">{inp("max_patients", { type: "number", min: 1 })}</Field>
              <Field label="Stockage (Mo)" required>{inp("storage_mb", { type: "number", min: 0 })}</Field>
            </>
          )}
        </div>
        {k === "modules" && <ModuleSelector modules={reference.modules} value={f.modules} onChange={(v) => set("modules", v)} planModules={plan.modules} />}
        <div className="flex justify-between gap-3 mt-8 pt-5 border-t border-slate-100">
          <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="tenant-form-prev">Précédent</Button>
          {step < steps.length - 1 ? (
            <Button type="button" onClick={next} data-testid="tenant-form-next">Continuer</Button>
          ) : (
            <Button type="button" onClick={submit} disabled={busy} data-testid="tenant-form-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{mode === "create" ? "Créer le cabinet" : "Enregistrer"}
            </Button>
          )}
        </div>
      </Panel>
    </div>
  );
};
